import { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  useAdminBookingsCatalog,
  useAdminServicesCatalog,
  useAdminStaffCatalog,
} from "@/hooks/useAdminPagedCatalogs";
import { useToast } from "@/hooks/useToast";
import Box from "@mui/material/Box";
import Pagination from "@mui/material/Pagination";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Skeleton from "@mui/material/Skeleton";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import InputAdornment from "@mui/material/InputAdornment";
import {
  Search,
  FileDownload,
  CalendarMonth,
  Add,
  NotificationsActive,
  Delete,
  AccessTime,
  Email,
} from "@mui/icons-material";
import { formatCents, formatDateTime, getStatusChipColor, sameEntityId } from "@/lib/utils";
import {
  bookingToEditPreset,
  computeAdminListRange,
  resolveOfferingName,
  resolveStaffDisplayName,
  type NewBookingDrawerPreset,
} from "@/lib/adminApi";
import { unwrapPagedList } from "@/lib/bookingApi";
import type { BookingWithDetails } from "@shared/schema";
import BookingDetailModal from "@/components/BookingDetailModal";
import NewBookingDrawer from "@/components/NewBookingDrawer";

type StatusFilter = "all" | "confirmed" | "pending_payment" | "completed" | "cancelled";

interface WaitlistEntry {
  id: string;
  email: string;
  serviceId: string;
  staffId: string | null;
  preferredDate: string;
  notes: string | null;
  notifiedAt: string | null;
  createdAt: string;
  service?: { name: string };
  staff?: { displayName: string };
}

/** OpenAPI default page size for GET `/v1/booking/admin/bookings`. */
const BOOKINGS_LIST_PAGE_SIZE = 50;

export default function AdminBookings() {
  const { t } = useTranslation();
  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const defaultStaff = params.get("staff") || "all";
  const { toast } = useToast();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [staffFilter, setStaffFilter] = useState<string>(defaultStaff);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedBooking, setSelectedBooking] = useState<BookingWithDetails | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [newBookingPreset, setNewBookingPreset] = useState<NewBookingDrawerPreset | undefined>(undefined);
  const [bookingsPage, setBookingsPage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const tab = useMemo(
    () => (new URLSearchParams(location.search).get("tab") === "waitlist" ? 1 : 0),
    [location.search],
  );
  const setTab = useCallback(
    (v: number) => {
      navigate(v === 1 ? "/admin/bookings?tab=waitlist" : "/admin/bookings");
    },
    [navigate],
  );

  const { bookings: allBookings, isLoading: loadingAllBookings } = useAdminBookingsCatalog({
    enabled: tab === 0,
  });

  useEffect(() => {
    setBookingsPage(1);
  }, [statusFilter, staffFilter, dateFrom, dateTo, search]);

  const { staff: staffList } = useAdminStaffCatalog();
  const { services: servicesCatalog } = useAdminServicesCatalog();

  const { data: waitlist, isLoading: loadingWaitlist } = useQuery<WaitlistEntry[]>({
    queryKey: ["/api/admin/waitlist"],
    select: (raw: unknown) =>
      unwrapPagedList<Record<string, unknown>>(raw).map((row, i) => ({
        id: String(row.id ?? i),
        email: String(row.customerEmail ?? row.email ?? ""),
        serviceId: String(row.serviceId ?? ""),
        staffId: row.staffId != null && row.staffId !== "" ? String(row.staffId) : null,
        preferredDate: String(row.requestedDate ?? row.preferredDate ?? ""),
        notes: row.notes != null ? String(row.notes) : null,
        notifiedAt: row.notifiedAt != null ? String(row.notifiedAt) : null,
        createdAt: String(row.createdAt ?? ""),
        service: undefined,
        staff: undefined,
      })),
  });

  const notifyMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/admin/waitlist/${id}/notify`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/waitlist"] });
      toast({ title: "Notification sent" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteWaitlistMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/waitlist/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/waitlist"] });
      toast({ title: t("toasts.removedFromWaitlist") });
    },
    onError: (err: Error) => toast({ title: t("common.error"), description: err.message, variant: "destructive" }),
  });

  const filtered = useMemo(() => allBookings
      .filter((b) => {
        if (statusFilter !== "all" && b.status !== statusFilter) return false;
        if (staffFilter !== "all") {
          const sid = b.staffId ?? null;
          if (staffFilter === "unassigned" && sid) return false;
          if (staffFilter !== "unassigned" && !sameEntityId(sid, staffFilter)) return false;
        }
        if (dateFrom) {
          const from = new Date(`${dateFrom  }T00:00:00`);
          if (new Date(b.startAt) < from) return false;
        }
        if (dateTo) {
          const to = new Date(`${dateTo  }T23:59:59`);
          if (new Date(b.startAt) > to) return false;
        }
        if (search) {
          const q = search.toLowerCase();
          const name = `${b.customer?.firstName} ${b.customer?.lastName}`.toLowerCase();
          const service = (resolveOfferingName(b, servicesCatalog) ?? "").toLowerCase();
          const email = b.customer?.email?.toLowerCase() || "";
          if (!name.includes(q) && !service.includes(q) && !email.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime()),
    [allBookings, statusFilter, staffFilter, dateFrom, dateTo, search, servicesCatalog],
  );

  const filteredListMeta = useMemo(() => {
    const total = filtered.length;
    const limit = BOOKINGS_LIST_PAGE_SIZE;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const page = Math.min(Math.max(1, bookingsPage), totalPages);
    return { totalRecords: total, totalPages, page, limit };
  }, [filtered.length, bookingsPage]);

  useEffect(() => {
    if (filteredListMeta.page !== bookingsPage) setBookingsPage(filteredListMeta.page);
  }, [filteredListMeta.page, bookingsPage]);

  const pagedFiltered = useMemo(() => {
    const start = (filteredListMeta.page - 1) * BOOKINGS_LIST_PAGE_SIZE;
    return filtered.slice(start, start + BOOKINGS_LIST_PAGE_SIZE);
  }, [filtered, filteredListMeta.page]);

  const bookingsListRange = computeAdminListRange(
    {
      totalRecords: filteredListMeta.totalRecords,
      page: filteredListMeta.page,
      limit: filteredListMeta.limit,
      totalPages: filteredListMeta.totalPages,
      currentPageSize: pagedFiltered.length,
    },
    pagedFiltered.length,
  );

  function exportCsv() {
    const headers = ["Date", "Customer", "Email", "Service", "Staff", "Status", "Price", "Source"];
    const rows = filtered.map((b) => [
      new Date(b.startAt).toLocaleDateString("en-GB"),
      `${b.customer?.firstName} ${b.customer?.lastName}`,
      b.customer?.email || "",
      resolveOfferingName(b, servicesCatalog) || "",
      resolveStaffDisplayName(b, staffList) || "Unassigned",
      b.status,
      ((b.service?.priceCents || 0) / 100).toFixed(2),
      b.source || "public",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const activeStaff = staffList?.filter((s) => s.active) ?? [];

  const statusOptions: { label: string; value: StatusFilter }[] = useMemo(
    () => [
      { label: t("adminBookings.statusAll"), value: "all" },
      { label: t("adminBookings.statusConfirmed"), value: "confirmed" },
      { label: t("adminBookings.statusPendingPayment"), value: "pending_payment" },
      { label: t("adminBookings.statusCompleted"), value: "completed" },
      { label: t("adminBookings.statusCancelled"), value: "cancelled" },
    ],
    [t],
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Typography variant="h5" fontWeight={700} data-testid="text-bookings-title">
          {t("adminBookings.title")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setNewBookingPreset(undefined);
              setNewBookingOpen(true);
            }}
            data-testid="button-new-booking"
          >
            {t("adminBookings.newBooking")}
          </Button>
          <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={exportCsv} data-testid="button-export-bookings">
            {t("adminBookings.exportCsv")}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "inline-flex",
          p: 0.5,
          mb: 2,
          borderRadius: 2,
          bgcolor: "action.hover",
          border: 1,
          borderColor: "divider",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 40,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              minHeight: 36,
              py: 0.5,
              px: 2,
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              color: "text.secondary",
              transition: (theme) =>
                theme.transitions.create(["background-color", "box-shadow", "color"], { duration: theme.transitions.duration.shorter }),
            },
            "& .Mui-selected": {
              color: "text.primary",
              bgcolor: "background.paper",
              boxShadow: 1,
            },
          }}
        >
          <Tab
            disableRipple
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {t("adminBookings.tabBookings")}
                <Chip size="small" label={filtered.length} sx={{ height: 22, "& .MuiChip-label": { px: 0.75 } }} />
              </Box>
            }
            data-testid="tab-bookings"
          />
          <Tab
            disableRipple
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {t("adminBookings.tabWaitlist")}
                {(waitlist?.length ?? 0) > 0 && (
                  <Chip size="small" label={waitlist?.length} sx={{ height: 22, "& .MuiChip-label": { px: 0.75 } }} />
                )}
              </Box>
            }
            data-testid="tab-waitlist"
          />
        </Tabs>
      </Box>

      {tab === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
            <TextField
              placeholder={t("adminBookings.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
              data-testid="input-search-bookings"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)} sx={{ minWidth: 180 }} data-testid="select-status-filter">
              {statusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField select label={t("adminBookings.staff")} value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)} sx={{ minWidth: 180 }} data-testid="select-staff-filter">
              <MenuItem value="all">{t("adminBookings.staffAll")}</MenuItem>
              <MenuItem value="unassigned">{t("adminBookings.staffUnassigned")}</MenuItem>
              {activeStaff.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.displayName}
                </MenuItem>
              ))}
            </TextField>
            <TextField type="date" label={t("adminBookings.from")} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} data-testid="input-date-from" />
            <TextField type="date" label={t("adminBookings.to")} value={dateTo} onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} data-testid="input-date-to" />
          </Box>

          {loadingAllBookings ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rounded" height={72} />
              ))}
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <CalendarMonth sx={{ fontSize: 48, color: "action.disabled", mb: 2 }} />
              <Typography color="text.secondary">{t("adminBookings.empty")}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t("adminBookings.emptyHint")}
              </Typography>
            </Box>
          ) : (
            <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {pagedFiltered.map((booking) => {
                const staffName = resolveStaffDisplayName(booking, staffList);
                return (
                <Card
                  key={booking.id}
                  variant="outlined"
                  sx={{ cursor: "pointer", "&:hover": { boxShadow: 2 } }}
                  onClick={() => {
                    setSelectedBooking(booking);
                    setDetailOpen(true);
                  }}
                  data-testid={`card-booking-${booking.id}`}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Typography fontWeight={600}>
                            {booking.customer?.firstName} {booking.customer?.lastName}
                          </Typography>
                          <Chip size="small" label={booking.status} color={getStatusChipColor(booking.status)} />
                          {booking.source === "admin" && <Chip size="small" label="Manual" variant="outlined" />}
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }} data-testid={`text-booking-ref-${booking.id}`}>
                            {`BK-${booking.id.slice(0, 8).toUpperCase()}`}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {resolveOfferingName(booking, servicesCatalog) ?? (
                            <em>{t("adminBookings.serviceRemoved")}</em>
                          )}
                          {staffName
                            ? ` · ${staffName}`
                            : ` · ${t("adminBookings.unassigned")}`}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateTime(booking.startAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCents(booking.service?.priceCents || 0, booking.currency || "GBP")}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
              })}
            </Box>
            {filteredListMeta.totalPages > 1 ? (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
                data-testid="box-bookings-pagination"
              >
                <Typography variant="body2" color="text.secondary" component="div">
                  {bookingsListRange
                    ? t("adminCommon.paginationRange", {
                        start: bookingsListRange.start,
                        end: bookingsListRange.end,
                        total: bookingsListRange.total,
                      })
                    : t("adminBookings.paginationSummary", {
                        page: filteredListMeta.page,
                        totalPages: filteredListMeta.totalPages,
                        total: String(filteredListMeta.totalRecords),
                      })}
                </Typography>
                <Pagination
                  color="primary"
                  size="small"
                  count={filteredListMeta.totalPages}
                  page={filteredListMeta.page}
                  onChange={(_, p) => setBookingsPage(p)}
                />
              </Box>
            ) : filteredListMeta.totalRecords > 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }} component="div">
                {t("adminBookings.paginationTotal", { count: filteredListMeta.totalRecords })}
              </Typography>
            ) : null}
            </>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {loadingWaitlist ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={72} />
              ))}
            </Box>
          ) : !waitlist?.length ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <NotificationsActive sx={{ fontSize: 48, color: "action.disabled", mb: 2 }} />
              <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
                {t("adminBookings.waitlistEmpty")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: "auto" }}>
                {t("adminBookings.waitlistHint")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {waitlist.map((entry) => (
                <Card key={entry.id} variant="outlined" data-testid={`card-waitlist-${entry.id}`}>
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Email fontSize="small" color="action" />
                          <Typography fontWeight={600}>{entry.email}</Typography>
                          {entry.notifiedAt && (
                            <Chip size="small" label={t("common.notified")} color="success" variant="outlined" />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                          <AccessTime fontSize="inherit" />
                          {entry.service?.name || "Any service"}
                          {entry.staff?.displayName && ` · ${entry.staff.displayName}`}
                          {entry.preferredDate &&
                            ` · ${new Date(entry.preferredDate).toLocaleDateString("en-GB", { weekday: "short", month: "short", day: "numeric" })}`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {!entry.notifiedAt && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<NotificationsActive />}
                            onClick={() => notifyMutation.mutate(entry.id)}
                            disabled={notifyMutation.isPending}
                            data-testid={`button-notify-waitlist-${entry.id}`}
                          >
                            {t("adminBookings.notify")}
                          </Button>
                        )}
                        <IconButton size="small" color="error" onClick={() => deleteWaitlistMutation.mutate(entry.id)} disabled={deleteWaitlistMutation.isPending} data-testid={`button-delete-waitlist-${entry.id}`}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}

      <BookingDetailModal
        booking={selectedBooking}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        staffList={staffList}
        onEdit={(b) => {
          setNewBookingPreset(bookingToEditPreset(b));
          setDetailOpen(false);
          setNewBookingOpen(true);
        }}
      />
      <NewBookingDrawer
        open={newBookingOpen}
        onOpenChange={(o) => {
          setNewBookingOpen(o);
          if (!o) setNewBookingPreset(undefined);
        }}
        preset={newBookingPreset}
      />
    </Box>
  );
}
